import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Package, Truck, MapPin, CheckCircle, Calculator, MessageSquare, User, ArrowRight, Shield, Clock, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import ThemeToggle from "@/components/ThemeToggle";
import SplashScreen from "@/components/SplashScreen";

const TrackingPage = () => {
  const [trackingNumber, setTrackingNumber] = useState("");
  const navigate = useNavigate();

  const handleTrack = (e) => {
    e.preventDefault();
    if (trackingNumber.trim()) {
      navigate(`/track/${trackingNumber.trim().toUpperCase()}`);
    }
  };

  const steps = [
    { icon: Package, title: "Received", desc: "Package registered at our warehouse" },
    { icon: Truck, title: "In Transit", desc: "On the way to destination" },
    { icon: MapPin, title: "Out for Delivery", desc: "Arriving at your doorstep" },
    { icon: CheckCircle, title: "Delivered", desc: "Successfully delivered" },
  ];

  const stats = [
    { value: "10K+", label: "Packages Delivered" },
    { value: "99.8%", label: "On-Time Rate" },
    { value: "2", label: "Office Locations" },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Splash Screen */}
      <SplashScreen />
      
      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo-v2.png" alt="SHIPP SMART" className="h-14 w-auto rounded-md" />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <Link to="/quote">
              <Button variant="ghost" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-sm font-medium">
                <Calculator className="w-4 h-4 mr-2" />
                Get Quote
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="ghost" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-sm font-medium">
                <MessageSquare className="w-4 h-4 mr-2" />
                Contact
              </Button>
            </Link>
            <ThemeToggle className="text-slate-600 dark:text-slate-400" />
            <Link to="/customer">
              <Button className="ml-2 bg-orange-600 hover:bg-orange-500 text-white text-sm font-medium rounded-lg" data-testid="customer-login-btn">
                <User className="w-4 h-4 mr-2" />
                Customer Portal
              </Button>
            </Link>
          </nav>
          <div className="flex md:hidden items-center gap-2">
            <ThemeToggle />
            <Link to="/customer">
              <Button size="sm" className="bg-orange-600 hover:bg-orange-500 text-white text-xs rounded-lg" data-testid="customer-login-btn">
                <User className="w-3.5 h-3.5 mr-1" /> Portal
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden pt-16">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.pexels.com/photos/14810115/pexels-photo-14810115.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/70 to-slate-900/90" />
        </div>

        <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-orange-400 font-medium text-sm tracking-[0.25em] uppercase mb-6">
              Precision Logistics — USA to HAITI
            </p>
            <h1 className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight mb-6 leading-[1.1]">
              Track Your <br className="hidden sm:block" />
              <span className="text-orange-500">Shipment</span>
            </h1>
            <p className="text-slate-300 text-base sm:text-lg max-w-xl mx-auto mb-10 leading-relaxed">
              Enter your tracking number for real-time updates on your package location and delivery status.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            onSubmit={handleTrack}
            className="mb-12"
          >
            <div className="flex flex-col sm:flex-row items-center w-full max-w-3xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-2 shadow-xl shadow-slate-900/10 mx-auto">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Enter tracking number (e.g., SHP1234567890)"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  className="w-full border-none bg-transparent pl-12 pr-4 py-4 text-lg focus:ring-0 focus:outline-none text-slate-900 dark:text-white placeholder:text-slate-400 font-mono"
                  data-testid="tracking-input"
                />
              </div>
              <Button
                type="submit"
                className="bg-orange-600 hover:bg-orange-500 text-white px-8 py-4 h-auto rounded-lg font-medium text-base w-full sm:w-auto mt-2 sm:mt-0 transition-colors"
                data-testid="track-btn"
              >
                Track Package
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.form>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="flex justify-center gap-12 sm:gap-16">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <p className="font-outfit font-bold text-2xl sm:text-3xl text-white">{s.value}</p>
                <p className="text-slate-400 text-xs sm:text-sm mt-1">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Tracking Steps */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <p className="text-orange-600 dark:text-orange-400 font-medium text-sm tracking-[0.2em] uppercase mb-3">How It Works</p>
            <h2 className="font-outfit font-bold text-2xl sm:text-3xl lg:text-4xl text-slate-900 dark:text-white tracking-tight">
              From Our Warehouse to Your Door
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-8 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 group"
              >
                <div className="absolute top-6 right-6 text-slate-200 dark:text-slate-800 font-outfit font-bold text-4xl">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="w-12 h-12 rounded-lg bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center mb-5 group-hover:bg-orange-100 dark:group-hover:bg-orange-500/20 transition-colors">
                  <step.icon className="w-6 h-6 text-orange-600 dark:text-orange-400" strokeWidth={1.5} />
                </div>
                <h3 className="font-outfit font-semibold text-lg text-slate-900 dark:text-white mb-2">{step.title}</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-20 px-6 bg-slate-900 dark:bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: "Secure Handling", desc: "Every package is handled with care from pickup to delivery. Full insurance coverage available." },
              { icon: Clock, title: "Fast & Reliable", desc: "Consistent transit times between Miami and Haiti with real-time tracking updates at every step." },
              { icon: Globe, title: "USA & Haiti", desc: "Two office locations to serve you — Miami warehouse and Petion-Ville distribution center." },
            ].map((item, i) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center md:text-left">
                <div className="w-12 h-12 rounded-lg bg-orange-600/10 flex items-center justify-center mb-5 mx-auto md:mx-0">
                  <item.icon className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
                </div>
                <h3 className="font-outfit font-semibold text-lg text-white mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 dark:bg-slate-900/80 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-3 gap-10 mb-12">
            <div>
              <img src="/logo-v2.png" alt="SHIPP SMART" className="h-12 w-auto mb-4 rounded-md" />
              <p className="text-slate-400 text-sm leading-relaxed max-w-xs">
                Precision logistics connecting USA and Haiti with reliable, transparent shipping services.
              </p>
            </div>
            <div>
              <h4 className="font-outfit font-semibold text-white text-sm tracking-wider uppercase mb-4">USA Office</h4>
              <p className="text-slate-400 text-sm">8020 NW 66th Street BP-137803</p>
              <p className="text-slate-400 text-sm">Miami, FL 33166-3900</p>
              <p className="text-slate-500 text-sm mt-2">Tel: (786) 360-6446</p>
            </div>
            <div>
              <h4 className="font-outfit font-semibold text-white text-sm tracking-wider uppercase mb-4">Haiti Office</h4>
              <p className="text-slate-400 text-sm">12 Imp Rodney Theocratie</p>
              <p className="text-slate-400 text-sm">Vivy Mitchell, Petion Ville Haiti</p>
              <p className="text-slate-500 text-sm mt-2">Tel: (509) 3720-9459</p>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">&copy; {new Date().getFullYear()} SHIPP SMART. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/quote" className="text-slate-500 hover:text-orange-500 text-sm transition-colors">Get Quote</Link>
              <Link to="/contact" className="text-slate-500 hover:text-orange-500 text-sm transition-colors">Contact</Link>
              <Link to="/customer" className="text-slate-500 hover:text-orange-500 text-sm transition-colors">Customer Portal</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TrackingPage;
